import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import throttle from "lodash/throttle";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import {
  GET_ASSIGNABLE_ENFORCEMENT_INTENT_OPERATORS,
  GetAssignableEnforcementIntentOperatorsData,
  GetAssignableEnforcementIntentOperatorsVars,
  AssignableOperator,
} from "common/graphql/queries/GetAssignableEnforcementIntentOperators";

export interface OperatorSelectProps {
  /** Enforcement intent whose violated policy scopes the candidate operators */
  intentId: string;
  value: AssignableOperator | null;
  onChange: (operator: AssignableOperator | null) => void;
  disabled?: boolean;
}

/**
 * Searchable dropdown of fleet operators eligible for assignment on an
 * unpermitted-vehicle citation (AUT-5032). Candidates are policy-scoped
 * server-side; the search text filters by organization name. Assignment is
 * one-time, so the caller renders this only while the citation is unassigned.
 *
 * Renders no field label: every call site already sits under an "Operator"
 * heading in the session-details grid, so a TextField label would repeat it.
 */
export const OperatorSelect: React.FC<OperatorSelectProps> = ({ intentId, value, onChange, disabled = false }) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<AssignableOperator[]>([]);

  const [getOperators, { loading }] = useLazyQuery<
    GetAssignableEnforcementIntentOperatorsData,
    GetAssignableEnforcementIntentOperatorsVars
  >(GET_ASSIGNABLE_ENFORCEMENT_INTENT_OPERATORS, {
    onCompleted(data) {
      setOptions(data.assignable_enforcement_intent_operators);
    },
  });

  const fetch = useMemo(() => throttle(getOperators, 200), [getOperators]);

  // Cancel any deferred call on unmount: a throttled fetch scheduled while the
  // user was typing would otherwise fire after navigation and setOptions on an
  // unmounted component (Greptile, #1273).
  useEffect(() => () => fetch.cancel(), [fetch]);

  return (
    <Autocomplete<AssignableOperator, false>
      fullWidth
      size="small"
      loading={loading}
      disabled={disabled}
      value={value}
      options={options}
      getOptionLabel={(option) => option.organizationName}
      isOptionEqualToValue={(option, val) => option.accountId === val.accountId}
      onChange={(_, v) => onChange(v)}
      inputValue={inputValue}
      onOpen={() => {
        fetch({ variables: { intentId, search: inputValue } });
      }}
      onInputChange={(_, newInputValue, reason) => {
        setInputValue(newInputValue);
        // MUI fires 'reset' when a selection rewrites the input to the chosen
        // option's label; searching for the name we just picked is a wasted
        // round-trip that also burns the throttle slot (Greptile, #1273).
        if (reason !== "reset") {
          fetch({ variables: { intentId, search: newInputValue } });
        }
      }}
      renderInput={(params) => <TextField {...params} placeholder="Search by organization" />}
    />
  );
};

export default OperatorSelect;
